import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { captureThemeSnapshot, readLoginSnapshot, readThemeSnapshot } from "@/lib/theme-snapshot";

const ThemeCtx = createContext(null);

// Theme preference is PER-USER. A missing user preference must not inherit a
// guest/browser value: it must continue to resolve through tenant/platform
// defaults.
const KEY = (userId) => `analyzio_theme:${userId || "guest"}`;

// iter-121.147: returns the user's EXPLICIT stored preference ("dark"/"light")
// or null when they've never toggled on this browser. Null lets the org /
// platform Dashboard-Theme `theme_mode` default take over (wired via the
// "analyzio:theme-mode" event). We no longer coerce a "light" default here so
// a fresh session can honour the org theme_mode.
function readPersisted(userId) {
    try {
        if (!userId) return null;
        const v = localStorage.getItem(KEY(userId));
        if (v === "dark" || v === "light") return v;
    } catch { /* localStorage may be unavailable */ }
    return null;
}

export function ThemeProvider({ children }) {
    const auth = useAuth?.() || {};
    const userId = auth?.user?.id || null;
    // `pref` = explicit per-user override (null until the user toggles).
    // iter-121.151 — seed from the CURRENT user's slot (not the guest slot) so
    // an explicit-pref user's first render already resolves correctly and
    // doesn't momentarily strip the bootstrap's `.dark` class.
    const [pref, setPref] = useState(() => readPersisted(userId));
    // `orgMode` = org/platform theme_mode default from the Dashboard Theme.
    // iter-121.151 — seed the initial value from the persisted theme snapshot
    // (the resolved dark/light of the last session) so a user whose dark mode
    // comes from the ORG default (no explicit pref) still resolves to the
    // correct mode on the very first render — matching the inline <head>
    // bootstrap — instead of flashing light until the settings API returns.
    // The real theme_mode event overwrites this the moment it arrives.
    const [orgMode, setOrgMode] = useState(() => {
        // Guests use the cached public login snapshot; authenticated users
        // use their full snapshot. Both contain the resolved platform/tenant
        // default, never a missing-user fallback.
        const snap = readThemeSnapshot() || readLoginSnapshot();
        if (snap && typeof snap.dark === "boolean") return snap.dark ? "dark" : "light";
        return null;
    });
    // iter-121.148: OS-level preference, used only when orgMode === "system".
    const [systemDark, setSystemDark] = useState(() => {
        try { return window.matchMedia?.("(prefers-color-scheme: dark)").matches || false; }
        catch { return false; }
    });

    // Re-read the explicit pref whenever the auth user changes.
    useEffect(() => {
        setPref(readPersisted(userId));
    }, [userId]);

    // Absorb the org/platform theme_mode default. A user's explicit pref
    // still wins (see `theme` resolution below).
    useEffect(() => {
        const onMode = (e) => {
            const m = e?.detail;
            if (m === "dark" || m === "light" || m === "system") setOrgMode(m);
        };
        window.addEventListener("analyzio:theme-mode", onMode);
        return () => window.removeEventListener("analyzio:theme-mode", onMode);
    }, []);

    // Track OS dark-mode changes (only matters when orgMode === "system").
    useEffect(() => {
        let mq;
        try { mq = window.matchMedia("(prefers-color-scheme: dark)"); }
        catch { return undefined; }
        const onChange = (e) => setSystemDark(e.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);

    // Effective theme: explicit user override > org default (system→OS) > light.
    const resolvedOrg = orgMode === "system" ? (systemDark ? "dark" : "light") : orgMode;
    const theme = pref || resolvedOrg || "light";

    useEffect(() => {
        const root = document.documentElement;
        if (theme === "dark") root.classList.add("dark");
        else root.classList.remove("dark");
        // Approach A — keep the persisted snapshot's dark flag in sync so the
        // inline <head> bootstrap paints the correct mode before first paint.
        // Guarded by an authenticated user so a logged-out /login visit never
        // recreates a snapshot we just cleared on logout.
        if (auth?.user) captureThemeSnapshot();
    }, [theme, auth?.user]);

    // Persist ONLY explicit preferences (never the resolved default) so a
    // fresh session keeps following the org theme_mode until the user opts in.
    const applyPref = (next) => {
        if (!userId) return;
        setPref(next);
        try { localStorage.setItem(KEY(userId), next); } catch { /* noop */ }
    };
    const setTheme = (next) => {
        if (next === "dark" || next === "light") applyPref(next);
    };
    // Platform/tenant defaults are separate from explicit user preferences.
    // This is the synchronous state contract used by startup and auth pages;
    // it avoids making initial theme resolution depend on a window event.
    const setDefaultTheme = useCallback((next) => {
        setOrgMode(next === "dark" || next === "light" || next === "system" ? next : null);
    }, []);
    const toggle = () => applyPref(theme === "dark" ? "light" : "dark");

    return (
        <ThemeCtx.Provider value={{ theme, setTheme, setDefaultTheme, toggle }}>
            {children}
        </ThemeCtx.Provider>
    );
}

export const useTheme = () => useContext(ThemeCtx);
